import * as Sentry from '@sentry/react'
import { getCurrentUserId } from '../supabase/client'
import { getCloudSyncCounts, createCloudTask, createCloudTag, getCloudTags } from '../supabase/queries'
import { db, withSyncWrite } from '../db/indexeddb'
import { getPendingChanges, markAsFailed, clearSyncedItems } from './sync-queue'
import { pullRemoteEntities } from './sync-pull'
import { pushEntityChange } from './sync-push'
import { taskPullDescriptor, taskPushDescriptor, projectPullDescriptor, projectPushDescriptor, sectionPullDescriptor, sectionPushDescriptor, tagPullDescriptor, tagPushDescriptor, goalPullDescriptor, goalPushDescriptor } from './sync-entity-configs'
import { repairOrphanedTaskAssociations, purgeSyncedDeletions, purgeOrphanedCloudTags } from './sync-repair'
import { resolveCloudProjectId } from './sync-resolve'
import { detectForeignLocalData } from './ownership-guard'
import { wipeUserLocalState } from '../auth/wipeLocalState'
import type { SyncResult, SyncOptions } from './types'
import { createEmptySyncResult } from './types'
import type { SyncQueueItem } from '../db/models'
import { trackEvent } from '../analytics'
import { ANALYTICS_EVENTS } from '../analytics/events'

/**
 * Bidirectional Sync Engine
 * Orchestrates push/pull synchronization between local Dexie and Supabase cloud.
 */

/** Prevents concurrent sync operations */
let isSyncing = false

/** Debounced sync timer */
let syncTimer: ReturnType<typeof setTimeout> | null = null
let syncMaxTimer: ReturnType<typeof setTimeout> | null = null

const SYNC_DEBOUNCE_MS = 1_000
const SYNC_MAX_WAIT_MS = 5_000
/** While the user has interacted within the last `USER_QUIET_MS`, scheduled
 *  syncs slip — push/pull cycles serialise on Dexie + the main thread, and
 *  a sync that lands during a click chain stalls the next user action by
 *  hundreds of ms. SYNC_MAX_WAIT_MS still forces an upper bound (5 s). */
const USER_QUIET_MS = 600
let lastUserActionAt = 0

/** Mark "the user just did something". Mutation handlers call this so
 *  scheduled syncs wait for the user to settle before competing for the
 *  Dexie connection. Cheap — just bumps a timestamp. */
export function markUserAction(): void {
  lastUserActionAt = Date.now()
}

/**
 * Schedule a debounced sync. Multiple calls within SYNC_DEBOUNCE_MS are
 * batched into a single performSync call. If the user is actively
 * interacting (last action within USER_QUIET_MS), the timer reschedules
 * itself instead of firing — so user actions don't stall behind a sync.
 * A sync is forced after SYNC_MAX_WAIT_MS regardless.
 *
 * Every caller of `scheduleSync` is downstream of a user mutation, so
 * we mark a user action implicitly here. That way no caller has to
 * remember to invoke `markUserAction()` separately.
 */
export function scheduleSync(): void {
  markUserAction()
  if (syncTimer) clearTimeout(syncTimer)
  const tick = () => {
    const sinceUser = Date.now() - lastUserActionAt
    if (sinceUser < USER_QUIET_MS) {
      // User is still active — slip the timer, but keep the max-wait
      // safety net intact so push/pull eventually fires.
      syncTimer = setTimeout(tick, USER_QUIET_MS - sinceUser)
      return
    }
    syncTimer = null
    if (syncMaxTimer) { clearTimeout(syncMaxTimer); syncMaxTimer = null }
    performSync().catch(() => {})
  }
  syncTimer = setTimeout(tick, SYNC_DEBOUNCE_MS)

  if (!syncMaxTimer) {
    syncMaxTimer = setTimeout(() => {
      if (syncTimer) { clearTimeout(syncTimer); syncTimer = null }
      syncMaxTimer = null
      performSync().catch(() => {})
    }, SYNC_MAX_WAIT_MS)
  }
}

/** Yield once to the event loop so React + Dexie LiveQuery callbacks
 *  scheduled during the prior pull step get a turn before the next
 *  pull starts. Cheap (~ms-scale via macrotask). */
export function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

/** Reset internal state (for tests only) */
export function _resetSyncState(): void {
  isSyncing = false
  lastUserActionAt = 0
  if (syncTimer) { clearTimeout(syncTimer); syncTimer = null }
  if (syncMaxTimer) { clearTimeout(syncMaxTimer); syncMaxTimer = null }
}

/**
 * Perform full bidirectional sync.
 * Prefer using scheduleSync() for user-triggered actions.
 */
export async function performSync(options: SyncOptions = {}): Promise<SyncResult> {
  const result = createEmptySyncResult()

  if (isSyncing) {
    result.errors.push('Sync already in progress')
    return result
  }

  isSyncing = true
  try {
    const userId = await getCurrentUserId()
    if (!userId) {
      result.errors.push('No user ID - offline mode')
      return result
    }

    // Ownership guard: if any local record is owned by a different user,
    // Dexie is poisoned from an unclean account switch and we must wipe
    // BEFORE any sync operation runs — otherwise the reconciler could push
    // foreign records to the current user's cloud account.
    const ownership = await detectForeignLocalData(userId)
    if (ownership.hasForeignData) {
      console.warn(
        '[Sync] Foreign local data detected — wiping Dexie before sync',
        ownership.counts,
        'foreign users:',
        ownership.foreignUserIds,
      )
      Sentry.captureMessage('[Sync] Foreign data wipe triggered', {
        level: 'warning',
        extra: {
          counts: ownership.counts,
          foreignUserIds: ownership.foreignUserIds,
          currentUserId: userId,
        },
      })
      await wipeUserLocalState()
      // Skip this cycle entirely; the next regular sync will run against a
      // clean DB without any race conditions with the current in-flight cycle.
      return result
    }

    const tables = options.tables || ['tags', 'projects', 'sections', 'tasks', 'goals', 'settings']

    // 0. Auto-queue unsynced tags, with name-based deduplication against cloud
    const allLocalTags = await db.tags.toArray()
    const unsyncedTags = allLocalTags.filter(t => !t.cloudId && !t.deletedAt && !t.synced)

    if (unsyncedTags.length > 0) {
      // Fetch cloud tags to check for duplicates BEFORE pushing
      const cloudTags = await getCloudTags(userId)
      const cloudTagNames = new Map(cloudTags.map(t => [t.name.toLowerCase(), t]))
      const localSyncedTags = allLocalTags.filter(t => !!t.cloudId && !t.deletedAt)

      for (const tag of unsyncedTags) {
        const cloudDuplicate = cloudTagNames.get(tag.name.toLowerCase())
        const localSyncedDuplicate = localSyncedTags.find(t => t.name.toLowerCase() === tag.name.toLowerCase())

        if (cloudDuplicate || localSyncedDuplicate) {
          // Tag with same name exists in cloud — merge locally
          const mergeTargetId = localSyncedDuplicate?.id
          if (mergeTargetId) {
            // Re-map task references from old local ID to the synced local ID
            const tasksWithOldTag = await db.tasks.filter(t => t.tags.includes(tag.id)).toArray()
            for (const task of tasksWithOldTag) {
              const updatedTags = task.tags.map(id => id === tag.id ? mergeTargetId : id)
              await withSyncWrite(() => db.tasks.update(task.id, { tags: updatedTags }))
            }
          }
          // Delete the unsynced duplicate
          await withSyncWrite(() => db.tags.delete(tag.id))
        } else {
          await db.queueSyncChange('create', 'tags', { localId: tag.id })
        }
      }
    }

    const pendingChanges = await getPendingChanges()

    // 1. Push ALL pending local changes
    const pushResult = await pushLocalChanges(userId, pendingChanges)
    result.synced.created += pushResult.created
    result.synced.updated += pushResult.updated
    result.synced.deleted += pushResult.deleted
    result.errors.push(...pushResult.errors)
    if (pushResult.created || pushResult.updated || pushResult.deleted || pushResult.errors.length) {
      console.log(`[Sync] PUSH result — created: ${pushResult.created}, updated: ${pushResult.updated}, deleted: ${pushResult.deleted}, errors: ${pushResult.errors.length}`)
    }

    // Fetch cloud entity counts once (lightweight RPC) for reconciliation gating
    const cloudCounts = await getCloudSyncCounts(userId)

    // 2. Pull remote changes per table.
    // We `yieldToEventLoop()` between table-pulls so React + Dexie
    // LiveQuery callbacks queued during one pull get to run before the
    // next one starts (each pull holds the IDB transaction and the JS
    // thread for hundreds of ms when there are many rows). Without the
    // yield, a sync that lands during a click chain stalls the next user
    // action by the entire serialised pull duration.
    for (const table of tables) {
      if (table === 'tags') {
        const r = await pullRemoteEntities(userId, tagPullDescriptor, pendingChanges, cloudCounts?.tagCount ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      await yieldToEventLoop()
      if (table === 'tasks') {
        const r = await pullRemoteEntities(userId, taskPullDescriptor, pendingChanges, cloudCounts?.taskCount ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
        if (r.created || r.updated || r.errors.length) {
          console.log(`[Sync] PULL tasks — created: ${r.created}, updated: ${r.updated}, errors: ${r.errors.length}`)
        }
      }
      await yieldToEventLoop()
      if (table === 'projects') {
        const r = await pullRemoteEntities(userId, projectPullDescriptor, pendingChanges, cloudCounts?.projectCount ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      await yieldToEventLoop()
      if (table === 'sections') {
        const r = await pullRemoteEntities(userId, sectionPullDescriptor, pendingChanges, cloudCounts?.sectionCount ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      await yieldToEventLoop()
      if (table === 'goals') {
        const r = await pullRemoteEntities(userId, goalPullDescriptor, pendingChanges, cloudCounts?.goalCount ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Settings sync can be added here later
    }

    // 3. Post-sync maintenance
    const repairResult = await repairOrphanedTaskAssociations(userId)
    result.synced.updated += repairResult.repaired

    const purgedTags = await purgeOrphanedCloudTags(userId)
    result.synced.deleted += purgedTags

    await clearSyncedItems(7)
    await purgeSyncedDeletions()

    result.success = result.errors.length === 0
    const { created, updated, deleted } = result.synced
    if (created || updated || deleted || result.errors.length) {
      console.log(`[Sync] done — created: ${created}, updated: ${updated}, deleted: ${deleted}${result.errors.length ? `, errors: ${result.errors.length}` : ''}`)
    }

    // Report sync errors to PostHog for production monitoring
    if (result.errors.length > 0) {
      const hasSchemaError = result.errors.some(e => e.includes('[SCHEMA]'))
      trackEvent(ANALYTICS_EVENTS.SYNC_ERROR, {
        error_count: result.errors.length,
        error_samples: result.errors.slice(0, 3),
        has_schema_error: hasSchemaError,
      })
      // Schema errors mean local DB is behind cloud schema — devs must
      // ship a Dexie version bump. Surface as Sentry error (not just a
      // PostHog metric) so it triggers an alert.
      if (hasSchemaError) {
        Sentry.captureMessage('[Sync] Schema error during sync — DB upgrade needed', {
          level: 'error',
          tags: { source: 'sync-engine', phase: 'performSync', kind: 'schema' },
          extra: { error_samples: result.errors.slice(0, 5) },
        })
      }
    }

    // Notify listeners (e.g. useTabManager) that sync is complete
    if (result.success) {
      window.dispatchEvent(new CustomEvent('sync:complete'))
    }

    return result
  } catch (error) {
    console.error('[Sync] performSync error:', error)
    result.errors.push(`Sync failed: ${error}`)
    trackEvent(ANALYTICS_EVENTS.SYNC_ERROR, {
      error_count: 1,
      error_samples: [`Sync failed: ${error}`],
      has_schema_error: false,
    })
    Sentry.captureException(error, {
      tags: { source: 'sync-engine', phase: 'performSync' },
    })
    return result
  } finally {
    isSyncing = false
  }
}

/**
 * Push local changes to cloud
 */
async function pushLocalChanges(
  userId: string,
  pendingChanges: SyncQueueItem[],
): Promise<{ created: number; updated: number; deleted: number; errors: string[] }> {
  const result = { created: 0, updated: 0, deleted: 0, errors: [] as string[] }

  try {
    // Defense-in-depth: drop any pending change whose local entity belongs
    // to a different user. Under normal conditions the engine-level
    // ownership guard catches this earlier, but this prevents a leak if
    // the guard is ever bypassed or a race condition occurs.
    const ownedChanges: SyncQueueItem[] = []
    for (const change of pendingChanges) {
      const localId = change.data?.localId
      if (!localId) {
        ownedChanges.push(change)
        continue
      }
      const table = (db as unknown as Record<string, { get: (id: string) => Promise<{ userId?: string | null } | undefined> }>)[change.table]
      if (!table?.get) {
        ownedChanges.push(change)
        continue
      }
      const local = await table.get(localId)
      if (local && local.userId != null && local.userId !== userId) {
        console.warn(
          `[Sync] Dropping push for foreign-owned ${change.table} ${localId} (owner: ${local.userId}, current: ${userId})`,
        )
        await markAsFailed(change.id!, `Foreign-owned record rejected by sync-push filter`)
        continue
      }
      ownedChanges.push(change)
    }

    // Sort: tags first, then projects, sections, tasks — so IDs can be resolved.
    // Goals are standalone (no FK dependency) so their order is irrelevant.
    const tableOrder: Record<string, number> = { tags: 0, projects: 1, sections: 2, tasks: 3, goals: 4 }
    const sorted = ownedChanges.toSorted((a, b) =>
      (tableOrder[a.table] ?? 9) - (tableOrder[b.table] ?? 9)
    )

    const descriptors: Record<string, typeof taskPushDescriptor> = {
      tags: tagPushDescriptor,
      tasks: taskPushDescriptor,
      projects: projectPushDescriptor,
      sections: sectionPushDescriptor,
      goals: goalPushDescriptor,
    }

    for (const change of sorted) {
      try {
        const descriptor = descriptors[change.table]
        if (descriptor) {
          await pushEntityChange(change, userId, result, descriptor)
        }
      } catch (error) {
        result.errors.push(`Failed to sync change ${change.id}: ${error}`)
        await markAsFailed(change.id!, String(error))
      }
    }

    return result
  } catch (error) {
    result.errors.push(`Push failed: ${error}`)
    return result
  }
}

/**
 * Initial sync (upload all local data to cloud)
 */
export async function initialSync(userId: string): Promise<SyncResult> {
  const result = createEmptySyncResult()

  try {
    const unsyncedTasks = await db.tasks
      .filter((task) => !task.cloudId && !task.deletedAt)
      .toArray()

    for (const task of unsyncedTasks) {
      try {
        const cloudProjectId = await resolveCloudProjectId(task.projectId)

        if (task.projectId && !cloudProjectId) {
          continue
        }

        const cloudTask = await createCloudTask(userId, {
          title: task.title,
          description: task.description,
          completed: task.status === 'completed',
          project_id: cloudProjectId,
          order: task.order,
          local_id: task.id,
        })

        if (cloudTask) {
          await withSyncWrite(() =>
            db.tasks.update(task.id, {
              userId,
              cloudId: cloudTask.id,
              synced: true,
              syncedAt: new Date(),
              updatedAt: new Date(cloudTask.created_at),
            })
          )
          result.synced.created++
        }
      } catch (error) {
        result.errors.push(`Failed to sync task ${task.id}: ${error}`)
      }
    }

    // Upload unsynced tags (created before sync queue was wired up)
    const unsyncedTags = await db.tags
      .filter((tag) => !tag.cloudId && !tag.deletedAt)
      .toArray()

    for (const tag of unsyncedTags) {
      try {
        const cloudTag = await createCloudTag(userId, {
          name: tag.name,
          color: tag.color,
          description: tag.description,
          usage_count: tag.usageCount,
          local_id: tag.id,
        })

        if (cloudTag) {
          await withSyncWrite(() =>
            db.tags.update(tag.id, {
              userId,
              cloudId: cloudTag.id,
              synced: true,
              syncedAt: new Date(),
              updatedAt: new Date(cloudTag.created_at),
            })
          )
          result.synced.created++
        }
      } catch (error) {
        result.errors.push(`Failed to sync tag ${tag.id}: ${error}`)
      }
    }

    result.success = result.errors.length === 0
    return result
  } catch (error) {
    result.errors.push(`Initial sync failed: ${error}`)
    return result
  }
}
